import React from 'react'

import { BlankSlate, Box, Skeleton, Tabs } from '@factorialco/deprecated-design-system'
import { PurchaseOrderDetailDocument } from 'generated/resources/graphql'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import Attachments from './Attachments'
import Footer from './Footer'
import Summary from './Summary'

interface Props {
  id: number
  open?: boolean
  onClose: () => void
}

const PurchaseOrderDetailPanel = ({ id, open = true, onClose }: Props) => {
  const { company } = useCurrentSession()
  const [currentTab, setCurrentTab] = React.useState('summary')
  const { data, error } = useQuery(PurchaseOrderDetailDocument, {
    variables: {
      companyId: company.id,
      ids: [id],
    },
  })

  const purchaseOrder = data?.procurement?.purchaseOrdersConnection?.nodes?.[0]

  const canManagePurchaseOrder = data?.procurement?._policies?.purchaseOrderManage.can ?? false

  const showFooter = purchaseOrder && currentTab === 'summary'

  return (
    <Sidepanel
      width="wide"
      title={i18n.t('procurement.purchase_orders.detail.title')}
      onClose={onClose}
      open={open}
      headerBorder
      closeLabel={i18n.t('common.close')}
      footer={canManagePurchaseOrder && showFooter && <Footer purchaseOrder={purchaseOrder} />}
      boxPadding={false}
    >
      <ErrorBoundary>
        {error ? (
          <Box alignItems="center" justifyContent="center" height="full">
            <BlankSlate
              illustration="alert"
              title={i18n.t('common.crash.title')}
              description={i18n.t('common.crash.retry')}
            />
          </Box>
        ) : !data && !error ? (
          <>
            <Box
              paddingLeft="s16"
              paddingRight="s16"
              className="flex-col flex-1 gap-8"
              alignItems="flexStart"
              justifyContent="flexStart"
            >
              <Box className="flex-col gap-2">
                <Skeleton width="s96" height="s24" />
                <Box className="flex-row gap-2">
                  <Skeleton width="s32" height="s24" />
                  <Skeleton width="s96" height="s24" />
                </Box>
              </Box>
              <Box className="flex-col gap-2">
                <Skeleton width="s120" height="s24" />
                <Box className="flex-row gap-2">
                  <Skeleton width="s24" height="s24" />
                  <Skeleton width="s240" height="s24" />
                </Box>
              </Box>
              <Box className="flex-col gap-2">
                <Skeleton width="s48" height="s24" />
                <Box className="flex-row gap-2">
                  <Skeleton width="s240" height="s24" />
                  <Skeleton width="s24" height="s24" />
                </Box>
              </Box>
            </Box>
          </>
        ) : (
          purchaseOrder && (
            <Box paddingTop="s6" overflowY="hidden">
              <Tabs
                currentId={currentTab}
                tabsAlign="flexStart"
                onTabClicked={(tabId: string) => setCurrentTab(tabId)}
                hasAdditionalPadding
              >
                <Tabs.Tab
                  id="summary"
                  label={i18n.t('procurement.purchase_orders.detail.tabs.summary')}
                >
                  <Summary purchaseOrder={purchaseOrder} />
                </Tabs.Tab>
                <Tabs.Tab
                  id="attachments"
                  label={i18n.t('procurement.purchase_orders.detail.tabs.invoices')}
                >
                  <Attachments
                    purchaseOrderId={purchaseOrder.id}
                    vendorId={purchaseOrder?.vendor?.id ?? undefined}
                  />
                </Tabs.Tab>
              </Tabs>
            </Box>
          )
        )}
      </ErrorBoundary>
    </Sidepanel>
  )
}

export default PurchaseOrderDetailPanel
